import type { Ring, Vec2 } from '../../../../geo/types';
import type { Frame, MeshBuilder, VertexStyle } from '../../../mesh/meshBuilder';
import type { Opening } from '../../openings';
import { pointedArchRise, pointedWindow } from '../../panels';
import { placeDetails, type FacadeContext } from '../../houseFacades';
import { edgeFace, edgePanel, openedWall, type MasonryStyles } from '../masonry';
import type { Arch } from '../arcade';
import type { PointedArch, StadhuisPlan } from './stadhuisPlan';
import { GLAZING } from '../tracery/glazing';

/** One town hall under construction: its plan, where it stands and its materials. */
export interface StadhuisBuild {
  readonly builder: MeshBuilder;
  readonly frame: Frame;
  readonly plan: StadhuisPlan;
  /** Pale stone of the vierschaar, tower and narrow gabled hall front, as painted. */
  readonly stone: MasonryStyles;
  /** The red-brown brick of the hall and the back wing. */
  readonly brick: MasonryStyles;
  /** The shade inside the loggia and the openings; walls, roof and openings all dark. */
  readonly dark: MasonryStyles;
  /** The glass of the cross windows, as on the houses (STYLE §8.1). */
  readonly glass: VertexStyle;
  /** Hands the hall's cross windows their joinery for the near tier (openingLod). */
  readonly facade: FacadeContext;
}

/** A pointed window centred at `center` along a wall, from `sill` to its apex at `top`. */
export function pointedAt(center: number, width: number, sill: number, top: number): Ring {
  return pointedWindow(center, sill, width, top - pointedArchRise(width), 3);
}

/** A plan arch as the arcade builder draws it: equilateral. */
export function gothic(arch: PointedArch, center: number = arch.center): Arch {
  return { center, width: arch.width, spring: arch.spring, pointed: true };
}

/** Stone pinnacles on the gables and the tower: shaft half-width, inset from the corner, heights over the eaves. */
export const PINNACLE = { half: 0.3, inset: 0.35, sink: 0.5, shaft: 2.6, tip: 5.3 } as const;

/** The flush dark hole of a window in its wall's panel coordinates. */
export function openingRing(o: Opening): Ring {
  return [
    [o.x, o.y],
    [o.x + o.width, o.y],
    [o.x + o.width, o.y + o.height],
    [o.x, o.y + o.height],
  ];
}

/** Rows of cross windows, one per bay of about `bay` metres along a wall of `length`. */
export function crossWindows(
  length: number,
  bay: number,
  rows: readonly { readonly sill: number; readonly top: number; readonly width: number }[]
): Opening[] {
  const count = Math.max(1, Math.round(length / bay));
  return rows.flatMap((row) =>
    Array.from({ length: count }, (_, k): Opening => ({
      kind: 'window',
      x: ((k + 0.5) * length) / count - row.width / 2,
      y: row.sill,
      width: row.width,
      height: row.top - row.sill,
    }))
  );
}

/** A wall to `outline` on a counter-clockwise plan edge, its cross windows cut and their joinery placed. */
export function windowedWall(
  s: StadhuisBuild,
  styles: MasonryStyles,
  edge: readonly [Vec2, Vec2],
  outline: Ring,
  windows: readonly Opening[]
): void {
  const glazed = { ...styles, opening: s.glass };
  openedWall(
    s.builder,
    glazed,
    s.frame,
    edgePanel(edge[0], edge[1]),
    outline,
    windows.map(openingRing)
  );
  const face = edgeFace(edge[0], edge[1]);
  // Keep the near-view joinery with the glass, behind the same physical reveals as the wall.
  const panel = styles.glazing
    ? {
        ...face.panel,
        origin: face.panel.origin.map((value, i) => value - face.outward[i]! * GLAZING.depth) as [
          number,
          number,
          number,
        ],
      }
    : face.panel;
  placeDetails(s.facade, { ...face, panel }, windows);
}
